import { useMemo } from 'react';
import { TitleLightWithSelectedParent } from '@warehouse/title/core';
import { WorkType } from '@warehouse/title-shared/core';
import { GridColDef } from '@mui/x-data-grid-pro';
import { SearchFieldsUtils } from '../../shared/ui/search-fields/search-fields-utils';

import {
	TitleCardColumnDataGrid,
	IDColumnDataGrid,
	RunLengthColumnDataGrid,
	ReleaseDateColumnDataGrid,
	SequenceNumberColumnDataGrid,
} from './data-grid-columns/common-columns';
import { EntryClassColumnDataGrid, EntryNumberColumnDataGrid } from './data-grid-columns/compilation-columns';
import { DistributionNumberColumnDataGrid, HouseSequenceColumnDataGrid } from './data-grid-columns/episode-columns';
import {
	EditClassesColumnDataGrid,
	EditDescriptionColumnDataGrid,
	EditUseColumnDataGrid,
	MadeForRegionsColumnDataGrid,
} from './data-grid-columns/edit-columns';
import {
	AlidColumnDataGrid,
	PlatformSpecificationColumnDataGrid,
	VersionLanguagesColumnDataGrid,
} from './data-grid-columns/manifestation-columns';

import { UseEditEntryNumberOutput } from './inline-edit/entry-number/use-edit-entry-number';
import { ChildrenTableType, getChildrenTableType } from './children-table-type';
import { ChildrenColumnName, isChildrenColumnName } from './children-sort';

export const getEntriesColumns = (
	editEntryNumber: UseEditEntryNumberOutput | null,
): GridColDef<TitleLightWithSelectedParent>[] => [
	TitleCardColumnDataGrid(null, 'relationship-tab-children'),
	EntryNumberColumnDataGrid(editEntryNumber),
	EntryClassColumnDataGrid(),
	RunLengthColumnDataGrid(),
	IDColumnDataGrid(),
];

export const getSeasonsColumns = (): GridColDef<TitleLightWithSelectedParent>[] => [
	TitleCardColumnDataGrid(),
	ReleaseDateColumnDataGrid(),
	SequenceNumberColumnDataGrid(),
	IDColumnDataGrid(),
];

export const getEpisodeColumns = (): GridColDef<TitleLightWithSelectedParent>[] => [
	TitleCardColumnDataGrid(600),
	ReleaseDateColumnDataGrid(),
	SequenceNumberColumnDataGrid(),
	DistributionNumberColumnDataGrid(),
	HouseSequenceColumnDataGrid(),
	RunLengthColumnDataGrid(),
	IDColumnDataGrid(),
];

export const getEditsColumns = (): GridColDef<TitleLightWithSelectedParent>[] => [
	TitleCardColumnDataGrid(),
	EditUseColumnDataGrid(),
	EditClassesColumnDataGrid(),
	EditDescriptionColumnDataGrid(),
	MadeForRegionsColumnDataGrid(),
	RunLengthColumnDataGrid(),
	IDColumnDataGrid(),
];

export const getManifestationsColumns = (): GridColDef<TitleLightWithSelectedParent>[] => [
	TitleCardColumnDataGrid(),
	PlatformSpecificationColumnDataGrid(),
	AlidColumnDataGrid(),
	MadeForRegionsColumnDataGrid(),
	VersionLanguagesColumnDataGrid(),
	RunLengthColumnDataGrid(),
	IDColumnDataGrid(),
];

export const defaultColumns: GridColDef<TitleLightWithSelectedParent>[] = [
	TitleCardColumnDataGrid(),
	IDColumnDataGrid(),
];

export function getChildrenColumns(
	tableType: ChildrenTableType,
	editEntryNumber: UseEditEntryNumberOutput | null,
): GridColDef<TitleLightWithSelectedParent>[] {
	switch (tableType) {
		case 'entries':
			return getEntriesColumns(editEntryNumber);
		case 'seasons':
			return getSeasonsColumns();
		case 'episodes':
			return getEpisodeColumns();
		case 'edits':
			return getEditsColumns();
		case 'manifestations':
			return getManifestationsColumns();
		default:
			return defaultColumns;
	}
}

export function getChildrenColumnNames(tableType: ChildrenTableType): ChildrenColumnName[] {
	return getChildrenColumns(tableType, null)
		.map(({ field }) => field)
		.filter(isChildrenColumnName);
}

export function useChildrenColumnsPerWorkType(
	parentWorkTypeUuid: string | undefined,
	childWorkType: WorkType,
	editEntryNumber?: UseEditEntryNumberOutput | null,
	columnVisibility?: { [key: string]: boolean },
): [GridColDef<TitleLightWithSelectedParent>[], string[]] {
	const [columns, searchFields] = useMemo(() => {
		const selectedColumns = getChildrenColumns(
			getChildrenTableType(parentWorkTypeUuid, childWorkType),
			editEntryNumber || null,
		);

		const dynamicSearchFields = SearchFieldsUtils.computeChildTableSearchFields(selectedColumns, columnVisibility);

		return [selectedColumns, dynamicSearchFields];
	}, [parentWorkTypeUuid, childWorkType, editEntryNumber, columnVisibility]);

	return [columns, searchFields];
}
